const TENANT_PREVIEW_STORAGE_KEY = "uniflo.previewTenantSlug";
const DEFAULT_SHARED_TENANT_HOSTS = ["uniflo.app", "www.uniflo.app"];
const SHARED_TENANT_RESERVED_SEGMENTS = new Set(
  [
    "account",
    "activate",
    "admin",
    "administrator",
    "api",
    "app",
    "alerts",
    "bonds",
    "call",
    "calendar",
    "case-studies",
    "conference",
    "connections",
    "commerce",
    "cost-of-scattered-work",
    "ethekwini-sdg-goals-portal",
    "event",
    "events",
    "finance",
    "files",
    "flows",
    "forgot",
    "friends",
    "get-started",
    "goals",
    "subclan",
    "subclans",
    "healthz",
    "home",
    "invite",
    "isimangaliso-case-study",
    "login",
    "meeting-action-tracker",
    "members",
    "messages",
    "mp3",
    "nihss-case-study",
    "notes",
    "notifications",
    "onboarding",
    "plans",
    "polls",
    "post",
    "pricing",
    "profile",
    "reset-password",
    "reports",
    "roles",
    "search",
    "site",
    "small-business-team-cohesion",
    "software-subscription-tracker",
    "static",
    "support",
    "team-alignment-template",
    "team-cohesion-app",
    "u",
    "usage",
    "subclan_messages",
    "uniflo",
    "uniflo-pricing",
    "uniflo-privacy",
    "uniflo-support",
    "uniflo-terms",
    "uniflo-vs-clickup",
    "uniflo-vs-microsoft-teams",
    "uniflo-vs-monday",
    "uniflo-vs-notion",
    "uniflo-vs-slack",
    "uniflo-vs-whatsapp-groups",
    "uploads",
    "video",
    "videos",
    "whatsapp-group-alternative-for-business",
  ].map((value) => `${value}`.trim().toLowerCase())
);

export function normalizeTenantPreviewHost(host = "") {
  return `${host || ""}`
    .trim()
    .toLowerCase()
    .replace(/:\d+$/, "");
}

function normalizeTenantPreviewSlug(slug = "") {
  return `${slug || ""}`.trim().toLowerCase();
}

function normalizePathname(pathname = "/") {
  const rawPath = `${pathname || ""}`.trim();
  if (!rawPath) return "/";
  const cleanPath = rawPath.split("?")[0].split("#")[0] || "/";
  return cleanPath.startsWith("/") ? cleanPath : `/${cleanPath}`;
}

function getRootPathSegment(pathname = "/") {
  const segments = normalizePathname(pathname).split("/").filter(Boolean);
  if (!segments.length) {
    return "";
  }

  return normalizeTenantPreviewSlug(segments[0]);
}

function isReservedSharedTenantRootPath(pathname = "/") {
  const rootSegment = getRootPathSegment(pathname);
  return Boolean(rootSegment && SHARED_TENANT_RESERVED_SEGMENTS.has(rootSegment));
}

export function getTenantPreviewHost() {
  if (typeof window === "undefined") return "";
  return normalizeTenantPreviewHost(window.location.host);
}

export function isTenantPreviewHost(host = getTenantPreviewHost()) {
  const normalizedHost = normalizeTenantPreviewHost(host);
  if (!normalizedHost) return false;
  if (DEFAULT_SHARED_TENANT_HOSTS.includes(normalizedHost)) {
    return true;
  }
  if (normalizedHost === "localhost" || normalizedHost === "127.0.0.1") {
    return true;
  }
  if (normalizedHost.endsWith(".up.railway.app")) {
    return true;
  }

  const extraSharedHosts = `${process.env.REACT_APP_UNIFLO_SHARED_TENANT_HOSTS || ""}`
    .split(",")
    .map((value) => normalizeTenantPreviewHost(value))
    .filter(Boolean);
  if (extraSharedHosts.includes(normalizedHost)) {
    return true;
  }

  const extraPreviewHosts = `${process.env.REACT_APP_UNIFLO_PREVIEW_TENANT_HOSTS || ""}`
    .split(",")
    .map((value) => normalizeTenantPreviewHost(value))
    .filter(Boolean);

  return extraPreviewHosts.includes(normalizedHost);
}

export function isCustomTenantHost(host = getTenantPreviewHost()) {
  const normalizedHost = normalizeTenantPreviewHost(host);
  return Boolean(normalizedHost && !isTenantPreviewHost(normalizedHost));
}

export function getTenantPreviewSlugFromSearch(
  search = typeof window !== "undefined" ? window.location.search : ""
) {
  return normalizeTenantPreviewSlug(new URLSearchParams(search).get("tenant") || "");
}

export function getTenantPreviewSlugFromPath(
  pathname = typeof window !== "undefined" ? window.location.pathname : ""
) {
  const segments = normalizePathname(pathname).split("/").filter(Boolean);
  if (!segments.length) {
    return "";
  }

  const candidate = normalizeTenantPreviewSlug(segments[0]);
  if (!candidate || SHARED_TENANT_RESERVED_SEGMENTS.has(candidate)) {
    return "";
  }

  return candidate;
}

export function stripTenantPreviewPrefix(
  pathname = typeof window !== "undefined" ? window.location.pathname : "",
  slug = getTenantPreviewSlugFromPath(pathname)
) {
  const normalizedPath = normalizePathname(pathname);
  const normalizedSlug = normalizeTenantPreviewSlug(slug);

  if (!normalizedSlug) {
    return normalizedPath;
  }
  if (normalizedPath === `/${normalizedSlug}`) {
    return "/";
  }
  if (normalizedPath.startsWith(`/${normalizedSlug}/`)) {
    return normalizedPath.slice(normalizedSlug.length + 1) || "/";
  }

  return normalizedPath;
}

export function getStoredTenantPreviewSlug() {
  if (typeof window === "undefined") return "";

  try {
    return normalizeTenantPreviewSlug(
      window.localStorage.getItem(TENANT_PREVIEW_STORAGE_KEY) || ""
    );
  } catch {
    return "";
  }
}

export function clearStoredTenantPreviewSlug() {
  if (typeof window === "undefined") return;

  try {
    window.localStorage.removeItem(TENANT_PREVIEW_STORAGE_KEY);
  } catch {
    // best-effort only
  }
}

export function storeTenantPreviewSlug(slug) {
  if (typeof window === "undefined") return;

  try {
    const normalizedSlug = normalizeTenantPreviewSlug(slug);
    if (!normalizedSlug) {
      clearStoredTenantPreviewSlug();
      return;
    }
    window.localStorage.setItem(TENANT_PREVIEW_STORAGE_KEY, normalizedSlug);
  } catch {
    // best-effort only
  }
}

function shouldPrefixSharedTenantPath(pathname = "/") {
  const normalizedPath = normalizePathname(pathname);

  if (normalizedPath === "/") return false;
  if (normalizedPath.startsWith("/app")) return false;
  if (normalizedPath.startsWith("/uniflo")) return false;
  if (normalizedPath === "/plans" || normalizedPath.startsWith("/plans/")) {
    return false;
  }
  if (
    normalizedPath === "/pricing" ||
    normalizedPath.startsWith("/pricing/") ||
    normalizedPath === "/onboarding" ||
    normalizedPath.startsWith("/onboarding/") ||
    normalizedPath === "/get-started" ||
    normalizedPath.startsWith("/get-started/")
  ) {
    return false;
  }

  return true;
}

function buildSharedTenantPath(pathname = "/", slug = "") {
  const normalizedPath = normalizePathname(pathname);
  const normalizedSlug = normalizeTenantPreviewSlug(slug);

  if (!normalizedSlug) return normalizedPath;
  if (normalizedPath === "/home") {
    return `/${normalizedSlug}`;
  }
  if (normalizedPath === "/") {
    return `/${normalizedSlug}`;
  }
  if (!shouldPrefixSharedTenantPath(normalizedPath)) return normalizedPath;
  if (
    normalizedPath === `/${normalizedSlug}` ||
    normalizedPath.startsWith(`/${normalizedSlug}/`)
  ) {
    return normalizedPath;
  }

  return `/${normalizedSlug}${normalizedPath}`;
}

export function shouldCarryTenantPreview(
  pathname = typeof window !== "undefined" ? window.location.pathname : ""
) {
  if (!isTenantPreviewHost()) return false;

  const tenantSlug = getTenantPreviewSlugFromPath(pathname);
  if (!tenantSlug && isReservedSharedTenantRootPath(pathname)) {
    return false;
  }

  const pathWithoutTenant = stripTenantPreviewPrefix(pathname, tenantSlug);
  if (pathWithoutTenant === "/account" || pathWithoutTenant.startsWith("/account/")) {
    return false;
  }

  if (pathWithoutTenant.startsWith("/app")) return false;
  if (pathWithoutTenant.startsWith("/uniflo")) return false;
  if (pathWithoutTenant === "/plans" || pathWithoutTenant.startsWith("/plans/")) {
    return false;
  }
  if (
    pathWithoutTenant === "/pricing" ||
    pathWithoutTenant.startsWith("/pricing/") ||
    pathWithoutTenant === "/onboarding" ||
    pathWithoutTenant.startsWith("/onboarding/") ||
    pathWithoutTenant === "/get-started" ||
    pathWithoutTenant.startsWith("/get-started/")
  ) {
    return false;
  }

  return true;
}

export function getTenantPreviewSlug() {
  if (typeof window === "undefined") return "";
  if (!isTenantPreviewHost()) return "";

  const slugFromPath = getTenantPreviewSlugFromPath(window.location.pathname);
  if (slugFromPath) {
    return slugFromPath;
  }

  const slugFromSearch = getTenantPreviewSlugFromSearch();
  if (slugFromSearch) {
    return slugFromSearch;
  }

  if (!shouldCarryTenantPreview(window.location.pathname)) {
    return "";
  }

  return getStoredTenantPreviewSlug();
}

export function appendTenantPreviewParam(value, slug = getTenantPreviewSlug()) {
  const normalizedSlug = normalizeTenantPreviewSlug(slug);
  if (!normalizedSlug) return value;

  const base =
    typeof window !== "undefined"
      ? window.location.origin
      : "https://uniflo.app";
  const url = new URL(value, base);
  const shouldApply =
    !/^https?:\/\//i.test(value) ||
    (typeof window !== "undefined" && url.origin === window.location.origin);

  if (!shouldApply) {
    return /^https?:\/\//i.test(value) ? url.toString() : value;
  }

  if (url.pathname.startsWith("/api/")) {
    url.searchParams.set("tenant", normalizedSlug);
  } else {
    url.pathname = buildSharedTenantPath(url.pathname, normalizedSlug);
    url.searchParams.delete("tenant");
  }

  if (/^https?:\/\//i.test(value)) {
    return url.toString();
  }

  return `${url.pathname}${url.search}${url.hash}`;
}

export function buildTenantRelativePath(path, slug = getTenantPreviewSlug()) {
  const normalizedPath = normalizePathname(path);
  const normalizedSlug = normalizeTenantPreviewSlug(slug);
  if (
    normalizedPath === "/home" &&
    !normalizedSlug &&
    !isCustomTenantHost()
  ) {
    return "/account";
  }
  return appendTenantPreviewParam(path, slug);
}

export function buildTenantAbsoluteUrl(path, slug = getTenantPreviewSlug()) {
  const base =
    typeof window !== "undefined"
      ? window.location.origin
      : "https://uniflo.app";
  return appendTenantPreviewParam(new URL(path, base).toString(), slug);
}

export function buildTenantUserPath(
  username,
  suffix = "",
  fallback = "/profile",
  slug = getTenantPreviewSlug()
) {
  const normalizedUsername = `${username || ""}`.trim();
  if (!normalizedUsername) {
    return fallback === "#" ? "#" : buildTenantRelativePath(fallback, slug);
  }

  const normalizedSuffix = `${suffix || ""}`.trim().replace(/^\/+/, "");
  const path = normalizedSuffix
    ? `/u/${normalizedUsername}/${normalizedSuffix}`
    : `/u/${normalizedUsername}`;

  return buildTenantRelativePath(path, slug);
}
